/**
 * Alta de cliente (Gestión → Clientes).
 *
 * Puerto del modal «Nuevo Cliente» del panel anterior (`/cliente/post`): DNI o
 * RUC, número de documento, nombre, nombre comercial, dirección, teléfono y
 * correo. La API rechaza un documento ya registrado con el mismo mensaje del
 * panel anterior.
 *
 * La **consulta a SUNAT/RENIEC** (`Venta::consulta_sunat`) es el botón *Buscar*
 * que autocompleta nombre, dirección, estado y condición: primero mira la
 * cartera local y, si el documento no es cliente, pregunta al servicio de
 * consulta configurado en la ficha de la empresa. El DNI `00000000` responde
 * `CLIENTES VARIOS` sin consultar nada.
 */

import { Info, Search } from 'lucide-react';
import { useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { ApiError } from '@/lib/http/api-error';

import { useConsultaDocumento, useCrearCliente } from '../clientes.api';
import { TIPOS_DOCUMENTO, type NuevoCliente } from '../gestion.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const TIPOS_CLIENTE = ['Cliente Normal', 'Cliente Distribuidor'];

/** Formulario vacío (los campos opcionales quedan sin valor, como el original). */
function vacio(): NuevoCliente {
  return { tipo_doc: '1', numero_doc: '', nombre: '', tipo: TIPOS_CLIENTE[0] };
}

interface Props {
  onClose: () => void;
  /** Mensaje de éxito que muestra el listado. */
  onAviso: (mensaje: string) => void;
}

export function ClienteNuevoDialog({ onClose, onAviso }: Props) {
  const crear = useCrearCliente();
  const consulta = useConsultaDocumento();
  const [datos, setDatos] = useState<NuevoCliente>(vacio);
  const [error, setError] = useState<string | null>(null);
  const [avisoConsulta, setAvisoConsulta] = useState<string | null>(null);

  const cambiar = <C extends keyof NuevoCliente>(campo: C, valor: NuevoCliente[C]) => {
    setDatos((previo) => ({ ...previo, [campo]: valor }));
  };

  const tipo = TIPOS_DOCUMENTO.find((opcion) => opcion.valor === datos.tipo_doc);
  const largo = tipo?.longitud ?? 8;
  const etiquetaTipo = tipo?.etiqueta ?? 'DNI';

  const guardar = () => {
    setError(null);
    crear.mutate(datos, {
      onSuccess: (respuesta) => {
        onClose();
        onAviso(respuesta.mensaje);
      },
      onError: (fallo) =>
        setError(fallo instanceof ApiError ? fallo.message : 'No se pudo registrar el cliente.'),
    });
  };

  /**
   * Completa los datos del documento: sólo escribe los campos que el servicio
   * devuelve con valor, para no borrar lo que el usuario ya haya escrito.
   */
  const buscarDocumento = () => {
    setError(null);
    setAvisoConsulta(null);

    const numero = datos.numero_doc.trim();

    if (numero === '') {
      setAvisoConsulta('Escribe el número de documento antes de buscarlo.');

      return;
    }

    consulta.mutate(
      { numero, tipo: datos.tipo_doc === '6' ? 'ruc' : 'dni' },
      {
        onSuccess: (respuesta) => {
          const documento = respuesta.documento;

          setDatos((previo) => ({
            ...previo,
            nombre: documento.nombre ?? previo.nombre,
            nombre_comercial: documento.nombre ?? previo.nombre_comercial,
            direccion: documento.direccion ?? previo.direccion,
          }));

          if (respuesta.origen === 'varios') {
            setAvisoConsulta('Documento genérico de consumidor final: CLIENTES VARIOS.');

            return;
          }

          const detalle = [documento.estado, documento.condicion].filter(Boolean).join(' · ');

          setAvisoConsulta(
            `${respuesta.origen === 'local' ? 'Datos tomados de la cartera de clientes' : 'Datos obtenidos del servicio de consulta'}${
              detalle ? ` · ${detalle}` : ''
            }.`,
          );
        },
        onError: (fallo) =>
          setAvisoConsulta(
            fallo instanceof ApiError ? fallo.message : 'No se pudo consultar el documento.',
          ),
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      size="md"
      title="Nuevo cliente"
      description="El documento no puede estar registrado en otro cliente."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button size="sm" loading={crear.isPending} onClick={guardar}>
            Registrar cliente
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? (
          <Alert variant="error" title="No se pudo registrar">
            {error}
          </Alert>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Tipo de documento</span>
            <Select
              className={CONTROL}
              value={datos.tipo_doc}
              onChange={(evento) => cambiar('tipo_doc', evento.target.value)}
            >
              {TIPOS_DOCUMENTO.map((tipo) => (
                <option key={tipo.valor} value={tipo.valor}>
                  {tipo.etiqueta}
                </option>
              ))}
            </Select>
          </label>

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                label="Número de documento"
                inputMode="numeric"
                placeholder={`${largo} dígitos para ${etiquetaTipo}`}
                value={datos.numero_doc}
                onChange={(evento) =>
                  // Se deja escribir libremente: el largo exacto lo valida la API (con
                  // su mensaje) en lugar de truncar el documento en silencio.
                  cambiar('numero_doc', evento.target.value.replace(/\D/g, '').slice(0, 20))
                }
              />
            </div>
            <Button
              variant="outline"
              leftIcon={<Search className="size-4" aria-hidden="true" />}
              loading={consulta.isPending}
              title="Buscar el documento en SUNAT/RENIEC"
              onClick={buscarDocumento}
            >
              Buscar
            </Button>
          </div>

          <Input
            label="Nombre"
            value={datos.nombre}
            onChange={(evento) => cambiar('nombre', evento.target.value)}
          />

          <Input
            label="Nombre comercial"
            value={datos.nombre_comercial ?? ''}
            onChange={(evento) => cambiar('nombre_comercial', evento.target.value)}
          />

          <Input
            label="Dirección"
            value={datos.direccion ?? ''}
            onChange={(evento) => cambiar('direccion', evento.target.value)}
          />

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Tipo de cliente</span>
            <Select
              className={CONTROL}
              value={datos.tipo}
              onChange={(evento) => cambiar('tipo', evento.target.value)}
            >
              {TIPOS_CLIENTE.map((opcion) => (
                <option key={opcion} value={opcion}>
                  {opcion}
                </option>
              ))}
            </Select>
          </label>

          <Input
            label="Teléfono"
            inputMode="numeric"
            value={datos.telefono ?? ''}
            onChange={(evento) =>
              cambiar('telefono', evento.target.value.replace(/\D/g, '').slice(0, 15))
            }
          />

          <Input
            label="Correo"
            type="email"
            value={datos.correo ?? ''}
            onChange={(evento) => cambiar('correo', evento.target.value)}
          />
        </div>

        {avisoConsulta ? (
          <Alert variant="info" title="Consulta de documento">
            {avisoConsulta}
          </Alert>
        ) : (
          <p className="flex items-start gap-2 text-xs text-ink-500">
            <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            El botón *Buscar* consulta el documento en la cartera de clientes y, si no está,
            en el servicio de SUNAT/RENIEC configurado en la empresa.
          </p>
        )}

        <Alert variant="info" title="Retenciones">
          <span className={ICONO_EN_ALERTA}>
            <Info className="size-4" aria-hidden="true" />
            Para RUC con retenciones, registra primero el cliente y actívalas en su ficha.
          </span>
        </Alert>
      </div>
    </Dialog>
  );
}
